/**
 * 任务中心 —— 全量任务视图（/mobile/tasks）。
 * 与首页（今日工作台）区分：这里承载完整的待处理 / 进行中 / 已完成 三态列表与历史回溯。
 *
 * 数据来源：后端 /fulfillment/todo 三 Tab（TO_DO / FOLLOWING / DONE）。
 * 三态映射：
 * - 待处理 = TO_DO（我该办，含 PENDING + IN_PROGRESS）
 * - 进行中 = TO_DO 中 status=IN_PROGRESS 的子集（前端二次筛选）
 * - 已完成 = DONE（我已办）
 *
 * 待办已由后端按当前登录用户过滤，前端不再按角色二次过滤。
 */
import { Box, Typography, CircularProgress } from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import TaskCard from '@/components/mobile/TaskCard';
import { getTodo } from '@/api/fulfillment';
import type { TodoItem } from '@/api/types';
import { MobileAppBar, IconBtn, SegTabs, SearchBox, EmptyState } from '@/components/mobile/ui';
import { T } from '@/theme/tokens';

type CenterTab = 'TODO' | 'DOING' | 'DONE';

export default function TaskPool() {
  const navigate = useNavigate();
  const location = useLocation();
  const initTab = (new URLSearchParams(location.search).get('tab') as CenterTab) || 'TODO';

  const [todoList, setTodoList] = useState<TodoItem[]>([]);
  const [doneList, setDoneList] = useState<TodoItem[]>([]);
  const [tab, setTab] = useState<CenterTab>(initTab);
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    // 待处理 / 已完成 独立加载，一个失败不影响另一个（避免整页进 error 分支变空白）
    Promise.allSettled([getTodo('TO_DO'), getTodo('DONE')])
      .then(([todoRes, doneRes]) => {
        if (!alive) return;
        setTodoList(todoRes.status === 'fulfilled' && Array.isArray(todoRes.value) ? todoRes.value : []);
        setDoneList(doneRes.status === 'fulfilled' && Array.isArray(doneRes.value) ? doneRes.value : []);
        if (todoRes.status === 'rejected' && doneRes.status === 'rejected') {
          setError('加载任务失败，请稍后重试');
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [reloadKey]);

  const inProgressList = useMemo(
    () => todoList.filter((t) => t.taskStatus === 'IN_PROGRESS'),
    [todoList],
  );

  const rawList =
    tab === 'TODO' ? todoList : tab === 'DOING' ? inProgressList : doneList;

  const list = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return rawList;
    return rawList.filter(
      (t) =>
        (t.orderNo || t.orderId || '').toLowerCase().includes(kw) ||
        (t.nodeName || t.nodeCode || '').toLowerCase().includes(kw),
    );
  }, [rawList, keyword]);

  const counts = {
    TODO: todoList.length,
    DOING: inProgressList.length,
    DONE: doneList.length,
  };

  const handleProcess = (task: TodoItem) => {
    const target =
      task.nodeCode === 'load' ? `/mobile/load/${task.orderId}` : `/mobile/task/${task.orderId}`;
    navigate(target, { state: { nodeCode: task.nodeCode, orderNo: task.orderNo || task.orderId } });
  };

  const emptyText =
    tab === 'DONE'
      ? { title: '暂无已完成任务', desc: '完成的节点任务会归档在这里' }
      : tab === 'DOING'
        ? { title: '暂无进行中任务', desc: '开始处理的订单会显示在这里' }
        : { title: '暂无待处理任务', desc: '新的节点任务派发后会出现在这里' };

  return (
    <Box>
      <MobileAppBar
        title="任务中心"
        right={
          <IconBtn label="刷新" onClick={() => setReloadKey((k) => k + 1)}>
            <RefreshIcon />
          </IconBtn>
        }
      />

      <Box sx={{ px: 2, pt: 2, pb: 3 }}>
        {/* 三态切换 */}
        <SegTabs<CenterTab>
          value={tab}
          onChange={setTab}
          options={[
            { value: 'TODO', label: '待处理', count: counts.TODO },
            { value: 'DOING', label: '进行中', count: counts.DOING },
            { value: 'DONE', label: '已完成', count: counts.DONE },
          ]}
          sx={{ mb: '14px' }}
        />

        {/* 搜索框 */}
        <Box sx={{ mb: '14px' }}>
          <SearchBox value={keyword} onChange={setKeyword} placeholder="搜索订单号 / 节点" />
        </Box>

        {/* 列表 */}
        {loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!loading && error && (
          <Typography sx={{ textAlign: 'center', color: T.danger, mt: 2, fontSize: '13px' }}>{error}</Typography>
        )}
        {!loading &&
          !error &&
          list.map((t) => (
            <TaskCard key={t.taskId || `${t.orderId}-${t.nodeCode}`} task={t} onAction={handleProcess} />
          ))}
        {!loading && !error && list.length === 0 && (
          <EmptyState
            icon={<AssignmentOutlinedIcon />}
            title={keyword.trim() ? '没有匹配的任务' : emptyText.title}
            desc={keyword.trim() ? '换个订单号或节点名称试试' : emptyText.desc}
          />
        )}
      </Box>
    </Box>
  );
}
